import { useFieldArray, useFormContext, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { AnalysisDisplayOption, Pipeline } from "../../../types/domain";
import { DropDownMultiSelect, SelectOption } from "../../common/Dropdown";
import { Label } from "../../common/FormLabel";
import { useEffect } from "react";

type PipelineAnalysisOptionsProps = {
  analysisOptions: SelectOption[];
  analysisDisplayOptions: AnalysisDisplayOption[];
  setValue: UseFormSetValue<Pipeline>;
  getValues: UseFormGetValues<Pipeline>;
};

export const PipelineAnalysisOptions = ({ analysisOptions, analysisDisplayOptions, setValue, getValues }: PipelineAnalysisOptionsProps) => {
  const { control, register} = useFormContext<Pipeline>();

  const { fields, replace } = useFieldArray({
    name: 'analysisDisplayOptions',
    control,
  });

  const selectedAnalysisOptions = (() => {
    const form = getValues() as Pipeline;
    const currentOptions = form.analysisDisplayOptions || [];
    
    return currentOptions.map((option: AnalysisDisplayOption) => ({
      value: option.index,
      label: option.fullName
    }));
  })();

  // Initialize with existing analysis display options if in edit mode
  useEffect(() => {
    const form = getValues() as Pipeline;
    const existingOptions = form.analysisDisplayOptions;

    if (existingOptions && existingOptions.length > 0) {
      replace(existingOptions);
    }
  }, []);
  
  const analysisOptionsChanged = (selectedOptions: SelectOption[]) => {
    const options = analysisDisplayOptions.filter((option: AnalysisDisplayOption) =>
      selectedOptions.some(selectedOption => selectedOption.value === option.index)
    );
  
    // Get the current values of the field array from the form context
    const form = getValues() as Pipeline;
    const fieldArrayValues = form.analysisDisplayOptions || [];
  
    const updatedOptions = options.map((option: AnalysisDisplayOption) => {
      const existingField = fieldArrayValues.find((field: AnalysisDisplayOption) => field.index === option.index);
  
      return existingField || {
        index: option.index,
        isMandatory: false,
        isReadonly: false,
        shortName: option.shortName,
        fullName: option.fullName,
      };
    });
  
    replace(updatedOptions);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, index: number, key: 'isMandatory' | 'isReadonly') => {
    const isChecked = e.target.checked;
    const toggleKey = key === 'isMandatory' ? 'isReadonly' : 'isMandatory'; //opposite value
  
    setValue(`analysisDisplayOptions.${index}.${key}`, isChecked);
  
    const form = getValues() as Pipeline;
    const currentToggleValue = form.analysisDisplayOptions?.[index]?.[toggleKey];
    
    if (isChecked && currentToggleValue === true) {
      setValue(`analysisDisplayOptions.${index}.${toggleKey}`, false);
    }
  };

  return (
    <>
      <div className="md:col-span-5">
        <Label forField='analysisFields' label='Analysis Fields' />
        <DropDownMultiSelect
          options={analysisOptions}
          onChange={analysisOptionsChanged}
          allOptionInResults={false}
          selected={selectedAnalysisOptions}
        />
        <table className="min-w-full divide-y-2 divide-gray-200 bg-white text-sm table-fixed mt-4">
          <thead className="bg-table-header border-t border-b border-table-border">
            <tr>
              <th className="px-4 py-2 font-semibold whitespace-nowrap text-table-header-foreground">Analysis Option</th>
              <th className="w-1/6 px-4 py-2 font-semibold whitespace-nowrap text-table-header-foreground text-center">ReadOnly</th>
              <th className="w-1/6 px-4 py-2 font-semibold whitespace-nowrap text-table-header-foreground text-center">Mandatory</th>
            </tr>
          </thead>
          {/** DYNAMIC ROWS */}
          <tbody className="divide-y divide-gray-200">
            {fields.map((field, index) => {
              return (
                <tr key={field.id}>
                  <td className="px-4 py-2 font-medium whitespace-nowrap text-gray-700">
                    {field.fullName}
                  </td>
                  <td className="w-1/6 px-4 py-2 text-center">
                    <input {...register(`analysisDisplayOptions.${index}.isReadonly`, {
                        onChange: (e) => handleCheckboxChange(e, index, "isReadonly"),
                    })} className="size-5 rounded-sm border-gray-300" type="checkbox" />
                  </td>
                  <td className="w-1/6 px-4 py-2 text-center">
                    <input {...register(`analysisDisplayOptions.${index}.isMandatory`, {
                      onChange: (e) => handleCheckboxChange(e, index, "isMandatory")
                    })} className="size-5 rounded-sm border-gray-300" type="checkbox" />
                  </td>
                </tr>
              );
            })}
          </tbody>

        </table>
      </div>
    </>
  );
};
